import i18n from '@/shared/i18n/i18n';
import { getIntlLocale } from '@/shared/i18n';

const EM_DASH = '—';

// NOTE: Intl formatters are constructed fresh on every call (not hoisted to module-level
// constants) so they pick up the active i18next language on every render, not just on module
// load — a hoisted formatter would freeze at whatever locale was active on first import.

export function formatDateTime(value?: string | null): string {
  if (!value) {
    return EM_DASH;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? EM_DASH
    : new Intl.DateTimeFormat(getIntlLocale(), { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

export function formatDate(value?: string | null): string {
  if (!value) {
    return EM_DASH;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? EM_DASH
    : new Intl.DateTimeFormat(getIntlLocale(), { dateStyle: 'medium' }).format(date);
}

/** Returns a trimmed value or an em-dash placeholder. */
export function detailLabel(value?: string | null): string {
  return value && value.trim().length > 0 ? value : EM_DASH;
}

export function fullName(firstName?: string | null, lastName?: string | null): string {
  return [firstName, lastName].filter((part) => part && part.trim().length > 0).join(' ');
}

/**
 * Best display name for a customer, falling back through the fields we have.
 *
 * Resolved via the i18next singleton (not `useTranslation`, since this is a plain
 * function, not a component) — see `catalog/lib/readiness.ts` for the same pattern.
 */
export function customerDisplayName(customer: {
  displayName?: string | null;
  firstName?: string | null;
  customerId: string;
}): string {
  return customer.displayName?.trim() || customer.firstName?.trim() || i18n.t('customers:format.unnamedCustomer');
}

/** Up-to-two-letter initials for an avatar. */
export function initials(name?: string | null): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '·';
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** `REGISTRATION_REQUIRED` → `Registration required`. */
export function humanizeCode(value?: string | null): string {
  if (!value) {
    return EM_DASH;
  }
  const normalized = value.replace(/[_-]+/g, ' ').trim().toLowerCase();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}
